import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import UserDataService from "./services/UserService";
import { useTable, useSortBy, usePagination } from "react-table";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  FormHelperText
} from "@mui/material";

// transaction services (root src/services)
import BusinessService from "../services/BusinessService";
import BroughtForwardModal from "../components/BroughtForwardModal";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

const Admin = (props) => {
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Active");
  const [assignedFilter, setAssignedFilter] = useState("All");
  const [officers, setOfficers] = useState([]);
  const [bfModalOpen, setBfModalOpen] = useState(false);
  const [selectedBfMember, setSelectedBfMember] = useState(null);

  const formatOfficerLabel = (off) => {
    if (!off) return "Officer";
    const offRoles = (off.roles || []).map((r) => (typeof r === "object" ? r.name : String(r)).toLowerCase());
    let rolePrefix = "Financial";
    if (offRoles.includes("financial_2")) rolePrefix = "Financial II";
    else if (offRoles.includes("financial_3")) rolePrefix = "Financial III";
    else if (offRoles.includes("financial_1") || offRoles.includes("financial")) rolePrefix = "Financial I";
    else if (offRoles.includes("admin")) rolePrefix = "Admin";
    else if (offRoles.includes("president")) rolePrefix = "President";

    const name = [off.firstName, off.lastName].filter(Boolean).join(" ").trim() || off.username || "Officer";
    return `${rolePrefix}: ${name}`;
  };

  const availableOfficers = useMemo(() => {
    const map = new Map();
    (officers || []).forEach((o) => {
      const id = String(o._id || o.id);
      if (id) map.set(id, o);
    });
    (users || []).forEach((u) => {
      (u.allocatedTo || []).forEach((o) => {
        if (typeof o === "object" && (o._id || o.id)) {
          const id = String(o._id || o.id);
          if (!map.has(id)) map.set(id, o);
        }
      });
    });
    return Array.from(map.values());
  }, [officers, users]);

  const assignedCounts = useMemo(() => {
    const counts = { All: users.length, unassigned: 0 };
    (users || []).forEach((u) => {
      const list = u.allocatedTo || [];
      if (!list || list.length === 0) {
        counts.unassigned = (counts.unassigned || 0) + 1;
      } else {
        list.forEach((o) => {
          const id = String(o._id || o.id || o);
          counts[id] = (counts[id] || 0) + 1;
        });
      }
    });
    return counts;
  }, [users]);

  const getAdminMemberStatus = (u) => {
    const raw = (u.approvedOrNotApproved || "Active").toString().trim().toLowerCase();
    if (raw === "withdraw" || raw === "withdrawn" || raw === "withdrawal") return "Withdraw";
    if (raw === "expel" || raw === "expelled") return "Expel";
    if (raw === "decease" || raw === "deceased") return "Decease";
    if (raw === "non-active" || raw === "inactive" || raw === "non active") return "Non-Active";
    return "Active";
  };

  const statusCounts = useMemo(() => {
    const counts = { Active: 0, "Non-Active": 0, Withdraw: 0, Expel: 0, Decease: 0, All: users.length };
    users.forEach((u) => {
      const st = getAdminMemberStatus(u);
      if (counts[st] !== undefined) counts[st]++;
      else counts.Active++;
    });
    return counts;
  }, [users]);

  const [searchTitle, setSearchTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const usersRef = useRef([]);

  // Logged-in user role detection
  const { user: reduxUser } = useSelector((state) => state.auth || {});
  const getStoredUser = () => {
    try {
      const s = localStorage.getItem("user") || localStorage.getItem("profile");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  };
  const currentUser = reduxUser || getStoredUser();

  const userRoles = useMemo(() => {
    const viewAsRole = sessionStorage.getItem("mtc_view_as_role");
    if (viewAsRole) {
      return [viewAsRole.toLowerCase().replace(/^role_/, "")];
    }
    const raw = currentUser?.roles || currentUser?.role || [];
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr.map((r) => {
      const s = typeof r === "object" && r !== null ? (r.name || "") : String(r);
      return s.toLowerCase().replace(/^role_/, "");
    });
  }, [currentUser]);

  const isAdmin = useMemo(() => {
    return userRoles.some((r) => r.includes("admin")) || Boolean(currentUser?.isAdmin);
  }, [userRoles, currentUser]);

  const isPresident = useMemo(() => {
    return userRoles.some((r) => r.includes("president")) || Boolean(currentUser?.isPresident);
  }, [userRoles, currentUser]);

  const isBusiness = useMemo(() => {
    return userRoles.some((r) => r.includes("business")) || Boolean(currentUser?.isBusiness);
  }, [userRoles, currentUser]);

  const isFin2 = userRoles.includes("financial_2");
  const isFin3 = userRoles.includes("financial_3");

  const isFinancialI = useMemo(() => {
    return (
      userRoles.some((r) => r === "financial" || r === "financial_1") ||
      (userRoles.some((r) => r.includes("financial")) && !isFin2 && !isFin3)
    );
  }, [userRoles, isFin2, isFin3]);

  const isSuperFinancial = useMemo(() => {
    return isAdmin || isFinancialI;
  }, [isAdmin, isFinancialI]);

  const isRestrictedFinancial = (isFin2 || isFin3) && !isSuperFinancial;

  const displayedUsers = useMemo(() => {
    let list = users;

    // 1. Search Query
    const q = (searchTitle || "").trim().toLowerCase();
    if (q) {
      list = list.filter((u) => {
        const fname = (u.firstName || u.firstname || u.first_name || u.name || "").toString().toLowerCase();
        const lname = (u.lastName || u.lastname || u.last_name || "").toString().toLowerCase();
        const uname = (u.username || "").toString().toLowerCase();
        const regNo = (u.registrationNo || u.membershipId || "").toString().toLowerCase();
        return fname.includes(q) || lname.includes(q) || uname.includes(q) || regNo.includes(q);
      });
    }

    // 2. Status Filter
    if (statusFilter !== "All") {
      list = list.filter((u) => getAdminMemberStatus(u) === statusFilter);
    }

    // 3. Assigned Officer Filter (skipped for Business role)
    if (!isBusiness) {
      if (assignedFilter === "unassigned") {
        list = list.filter((u) => !u.allocatedTo || u.allocatedTo.length === 0);
      } else if (assignedFilter !== "All") {
        list = list.filter((u) => {
          if (!u.allocatedTo || !Array.isArray(u.allocatedTo)) return false;
          return u.allocatedTo.some((o) => {
            const oId = String(o._id || o.id || o);
            return oId === String(assignedFilter);
          });
        });
      }
    }

    return list;
  }, [users, searchTitle, statusFilter, assignedFilter, isBusiness]);

  const roleTitle = useMemo(() => {
    if (userRoles.some((r) => r.includes("admin"))) return "Administrator (Full Access)";
    if (isBusiness) return "Business Officer (Business Account Only)";
    if (isFinancialI) return "Financial I (Full Access)";
    if (isFin2) return "Financial II (Assigned Members)";
    if (isFin3) return "Financial III (Assigned Members)";
    return "Financial Officer";
  }, [userRoles, isBusiness, isFinancialI, isFin2, isFin3]);

  // Allocation State (for Financial I)
  const [selectedOfficerId, setSelectedOfficerId] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(20);

  // Snackbar notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const [lastTx, setLastTx] = useState({
    savings: {},
    loan: {},
    shares: {},
    ileya: {},
    dev: {},
    min: {},
    wel: {},
    takaful: {},
    speacc: {},
    business: {},
    businessTenor: {},
    loanTenor: {}
  });

  usersRef.current = users;

  useEffect(() => {
    retrieveUsers();
    loadOfficers();
  }, []); // eslint-disable-line

  const loadOfficers = () => {
    UserDataService.getFinancialOfficers()
      .then((res) => {
        const list = res.data || [];
        // Strictly filter to ensure ONLY officers with financial role I, II, or III are displayed
        const finList = list.filter((o) => {
          const offRoles = (o.roles || []).map((r) => (typeof r === "object" ? r.name : String(r)).toLowerCase());
          return offRoles.some((r) => ["financial", "financial_1", "financial_2", "financial_3"].includes(r));
        });
        setOfficers(finList);
        if (list.length > 0 && !selectedOfficerId) {
          // Default to first Financial II or Financial III officer
          const sub = list.find((o) =>
            (o.roles || []).some((r) => ["financial_2", "financial_3"].includes(typeof r === "object" ? r.name : r))
          );
          if (sub) setSelectedOfficerId(sub._id || sub.id);
          else setSelectedOfficerId(list[0]._id || list[0].id);
        }
      })
      .catch((err) => console.error("loadOfficers error:", err));
  };

  const retrieveUsers = () => {
    setLoading(true);
    UserDataService.adminGetAll()
      .then((response) => {
        const u = response.data || [];
        setUsers(u);
        setLoading(false);
        fetchAllTransactions(u, setLastTx);
      })
      .catch((e) => {
        console.error("retrieveUsers error:", e);
        setLoading(false);
      });
  };

  const onChangeSearchTitle = (e) => {
    setSearchTitle(e.target.value);
  };

  // Range and Bulk selection helpers
  const handleSelectRange = () => {
    if (!users || users.length === 0) return;
    const startIdx = Math.max(0, parseInt(rangeStart, 10) - 1 || 0);
    const endIdx = Math.min(users.length - 1, parseInt(rangeEnd, 10) - 1 || 0);
    if (startIdx > endIdx) {
      setSnackbar({ open: true, message: "Invalid range: start index must be less than or equal to end index.", severity: "warning" });
      return;
    }
    const newSelected = new Set(selectedIds);
    for (let i = startIdx; i <= endIdx; i++) {
      const u = users[i];
      if (u && (u._id || u.id)) {
        newSelected.add(u._id || u.id);
      }
    }
    setSelectedIds(newSelected);
    setSnackbar({
      open: true,
      message: `Selected members ${startIdx + 1} to ${endIdx + 1} (${endIdx - startIdx + 1} members added to selection)`,
      severity: "info"
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((u) => u._id || u.id)));
    }
  };

  const toggleSelectOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleAllocate = (action = "assign") => {
    if (!selectedOfficerId) {
      setSnackbar({ open: true, message: "Please select a target Financial Officer (Financial II or III).", severity: "warning" });
      return;
    }
    if (selectedIds.size === 0) {
      setSnackbar({ open: true, message: "Please select at least one member to allocate.", severity: "warning" });
      return;
    }
    const officerObj = officers.find((o) => (o._id || o.id) === selectedOfficerId);
    const officerName = officerObj ? (officerObj.firstName || officerObj.username || "officer") : "officer";

    setAllocating(true);
    UserDataService.allocateMembers({
      officerId: selectedOfficerId,
      memberIds: Array.from(selectedIds),
      action
    })
      .then((res) => {
        setAllocating(false);
        setSnackbar({
          open: true,
          message: res.data?.message || (action === "unassign" ? `Unassigned members from ${officerName}` : `Successfully allocated ${selectedIds.size} members to ${officerName}`),
          severity: "success"
        });
        setSelectedIds(new Set());
        retrieveUsers();
      })
      .catch((err) => {
        setAllocating(false);
        setSnackbar({
          open: true,
          message: err?.response?.data?.error || err?.message || "Allocation operation failed.",
          severity: "error"
        });
      });
  };

  const renderAllocatedChip = (row) => {
    const list = row.allocatedTo || [];
    if (!list || list.length === 0) {
      return <Chip size="small" label="Unassigned" variant="outlined" sx={{ color: "text.secondary", fontSize: "0.75rem" }} />;
    }
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {list.map((officer) => {
          const offRoles = (officer.roles || []).map((r) => (typeof r === "object" ? r.name : String(r)));
          let label = "Financial";
          if (offRoles.includes("financial_2")) {
            label = "Fin II";
          } else if (offRoles.includes("financial_3")) {
            label = "Fin III";
          }
          const name = officer.firstName || officer.username || "Officer";
          return (
            <Chip
              key={officer._id || officer.id || Math.random()}
              size="small"
              color="primary"
              label={`${label}: ${name}`}
              sx={{ fontSize: "0.72rem", height: 22 }}
            />
          );
        })}
      </Box>
    );
  };

  const columns = useMemo(() => {
    const renderActionLink = (path, id, ts) => {
      const label = ts ? new Date(ts).toLocaleDateString() : "Open";
      return (
        <Link to={`/dashboard/${path}/${id}`} onClick={(e) => e.stopPropagation()} style={{ textDecoration: "none" }}>
          <Chip
            label={label}
            size="small"
            clickable
            sx={{
              height: 22,
              fontSize: "0.72rem",
              fontWeight: 600,
              backgroundColor: ts ? "rgba(5, 150, 105, 0.1)" : "rgba(11, 59, 96, 0.08)",
              color: ts ? "#047857" : "#0B3B60",
              borderRadius: "5px",
              border: "1px solid",
              borderColor: ts ? "rgba(5, 150, 105, 0.25)" : "rgba(11, 59, 96, 0.2)",
              "&:hover": {
                backgroundColor: ts ? "#059669" : "#0B3B60",
                color: "#ffffff",
              },
              transition: "all 0.15s ease-in-out",
            }}
          />
        </Link>
      );
    };

    const cols = [];

    // Business role can manage transactions on Business account ONLY
    if (isBusiness && !isAdmin) {
      cols.push(
        {
          Header: "#",
          id: "index",
          Cell: ({ row }) => {
            const absoluteIndex = row.index + 1;
            return <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>{absoluteIndex}</Typography>;
          },
          disableSortBy: true,
        },
        {
          Header: "Reg No",
          id: "membershipId",
          accessor: "membershipId",
          Cell: ({ value, row }) => (
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0B3B60" }}>
              {value || row.original.username || "—"}
            </Typography>
          ),
        },
        {
          Header: "Full Name",
          id: "fullName",
          accessor: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || row.username,
          Cell: ({ value, row }) => (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0F172A", lineHeight: 1.2 }}>
                {value}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748B" }}>
                @{row.original.username}
              </Typography>
            </Box>
          ),
        },
        {
          Header: "Phone",
          id: "phoneNumber",
          accessor: "phoneNumber",
          Cell: ({ value }) => (
            <Typography variant="body2" sx={{ color: "#334155", fontSize: "0.85rem" }}>
              {value || "—"}
            </Typography>
          ),
        },
        {
          Header: "Business",
          id: "business",
          accessor: (row) => row._id,
          Cell: ({ value }) => renderActionLink("business", value, lastTx.business ? lastTx.business[value] : null),
        },
        {
          Header: "Tenor",
          id: "businessTenor",
          accessor: (row) => (lastTx.businessTenor ? lastTx.businessTenor[row._id] : null),
          Cell: ({ value, row }) => {
            const hasBizDebt = Number(row?.original?.businessBalance) > 0;
            return value && hasBizDebt ? (
              <Chip
                label={`${value} Mos`}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  color: "#0D9488",
                  borderColor: "rgba(13, 148, 136, 0.4)",
                  backgroundColor: "rgba(13, 148, 136, 0.06)"
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ color: "#94A3B8" }}>—</Typography>
            );
          },
        },
        ...(!isPresident ? [{
          Header: "Brought Fwd",
          id: "broughtForward",
          Cell: ({ row }) => (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedBfMember(row.original);
                setBfModalOpen(true);
              }}
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                textTransform: "none",
                py: 0.25,
                px: 1,
                minWidth: 0,
                borderRadius: "6px",
                color: "#0B3B60",
                borderColor: "rgba(11, 59, 96, 0.3)",
                "&:hover": {
                  borderColor: "#0B3B60",
                  backgroundColor: "rgba(11, 59, 96, 0.06)",
                },
              }}
            >
              Set BF
            </Button>
          ),
          disableSortBy: true,
        }] : [])
      );
      return cols;
    }

    if (isSuperFinancial) {
      cols.push({
        id: "selection",
        Header: () => (
          <Checkbox
            size="small"
            checked={users.length > 0 && selectedIds.size === users.length}
            indeterminate={selectedIds.size > 0 && selectedIds.size < users.length}
            onChange={handleSelectAll}
            inputProps={{ "aria-label": "Select all members" }}
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              "&.Mui-checked": { color: "#ffffff" },
              "&.MuiCheckbox-indeterminate": { color: "#ffffff" },
            }}
          />
        ),
        Cell: ({ row }) => {
          const id = row.original._id || row.original.id;
          return (
            <Checkbox
              size="small"
              checked={selectedIds.has(id)}
              onChange={() => toggleSelectOne(id)}
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
        disableSortBy: true,
      });
    }

    cols.push(
      {
        Header: "#",
        id: "index",
        Cell: ({ row }) => {
          const absoluteIndex = row.index + 1;
          return <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>{absoluteIndex}</Typography>;
        },
        disableSortBy: true,
      },
      {
        Header: "Reg. No",
        accessor: (row) => row.registrationNo || row.membershipId || "-",
        disableSortBy: false,
      },
      {
        Header: "First Name",
        accessor: (row) => row.firstName || row.firstname || row.first_name || row.name || "",
        disableSortBy: false,
        sortType: "basic",
      },
      {
        Header: "Lastname",
        accessor: (row) => row.lastName || row.lastname || row.last_name || "",
        disableSortBy: false,
      },
      {
        Header: "Status",
        id: "status",
        accessor: (row) => getAdminMemberStatus(row),
        Cell: ({ row }) => {
          const st = getAdminMemberStatus(row.original);
          const bg =
            st === "Active" ? "#059669" :
            st === "Non-Active" ? "#D97706" :
            st === "Withdraw" ? "#854D0E" :
            st === "Expel" ? "#DC2626" :
            st === "Decease" ? "#475569" : "#059669";
          return (
            <Chip
              label={st}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: bg,
                color: "#FFFFFF",
              }}
            />
          );
        },
        disableSortBy: true,
      }
    );

    if (isSuperFinancial) {
      cols.push({
        Header: "Assigned To",
        id: "assignedTo",
        Cell: ({ row }) => renderAllocatedChip(row.original),
        disableSortBy: true,
      });
    }

    cols.push(
      {
        Header: "Shares",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("shares", value, lastTx.shares[value]),
      },
      {
        Header: "Savings",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("savings", value, lastTx.savings[value]),
      },
      {
        Header: "Loan",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("loan", value, lastTx.loan[value]),
      },
      {
        Header: "Loan Tenor",
        id: "loanTenor",
        accessor: (row) => (lastTx.loanTenor ? lastTx.loanTenor[row._id] : null),
        Cell: ({ value, row }) => {
          const hasLoanDebt = Number(row?.original?.loanBalance) > 0;
          return value && hasLoanDebt ? (
            <Chip
              label={`${value} Mos`}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                color: "#D97706",
                borderColor: "rgba(217, 119, 6, 0.4)",
                backgroundColor: "rgba(217, 119, 6, 0.05)"
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.8rem" }}>—</Typography>
          );
        },
      },
      {
        Header: "Ileya",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("ileyasavings", value, lastTx.ileya[value]),
      },
      {
        Header: "Dev. Levy",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("dev", value, lastTx.dev[value]),
      },
      {
        Header: "Minutes",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("min", value, null),
      },
      {
        Header: "Welfare",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("wel", value, null),
      },
      {
        Header: "Takaful",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("takaful", value, lastTx.takaful ? lastTx.takaful[value] : null),
      },
      {
        Header: "Special",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderActionLink("speacc", value, lastTx.speacc[value]),
      }
    );

    // Business column: ONLY visible to Admin (Financial I explicitly excluded from Business)
    if (isAdmin) {
      cols.push(
        {
          Header: "Business",
          id: "business",
          accessor: (row) => row._id,
          Cell: ({ value }) => renderActionLink("business", value, lastTx.business ? lastTx.business[value] : null),
        },
        {
          Header: "Biz Tenor",
          id: "businessTenor",
          accessor: (row) => (lastTx.businessTenor ? lastTx.businessTenor[row._id] : null),
          Cell: ({ value, row }) => {
            const hasBizDebt = Number(row?.original?.businessBalance) > 0;
            return value && hasBizDebt ? (
              <Chip
                label={`${value} Mos`}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  color: "#0D9488",
                  borderColor: "rgba(13, 148, 136, 0.4)",
                  backgroundColor: "rgba(13, 148, 136, 0.05)"
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.8rem" }}>—</Typography>
            );
          },
        }
      );
    }

    if (!isPresident) {
      cols.push({
        Header: "Brought Fwd",
        id: "broughtForward",
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBfMember(row.original);
              setBfModalOpen(true);
            }}
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "none",
              py: 0.25,
              px: 1,
              minWidth: 0,
              borderRadius: "6px",
              color: "#0B3B60",
              borderColor: "rgba(11, 59, 96, 0.3)",
              "&:hover": {
                borderColor: "#0B3B60",
                backgroundColor: "rgba(11, 59, 96, 0.06)",
              },
            }}
          >
            Set BF
          </Button>
        ),
        disableSortBy: true,
      });
    }

    return cols;
  }, [lastTx, isSuperFinancial, isBusiness, isAdmin, selectedIds, users]); // eslint-disable-line

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: displayedUsers,
      initialState: { pageIndex: 0, pageSize: 20 },
    },
    useSortBy,
    usePagination
  );

  if (!isAdmin && !isFinancialI && !isFin2 && !isFin3 && !isBusiness) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Access Restricted to Executive Officers
          </Typography>
          <Typography variant="body2">
            This page is only accessible to Executive Officers (Admin, Financial Officers, or Business Officer). Regular members can access their personal accounts (Savings, Loan, Shares, Business, etc.) directly from the navigation menu.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 2.5,
          border: "1px solid #E2E8F0",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)",
        }}
      >
        {/* Header Section */}
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.01em" }}>
                Financials — Members & Accounts
              </Typography>
              <Chip
                label={roleTitle}
                color={isSuperFinancial ? "primary" : "secondary"}
                variant="filled"
                size="small"
                sx={{ fontWeight: 700, borderRadius: "6px" }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5 }}>
              {isSuperFinancial
                ? "Manage member financial accounts and allocate member groups (e.g. 1-20) to Financial II and Financial III."
                : isBusiness
                ? `Manage business transactions for cooperative members (${users.length} members).`
                : `Managing member financial accounts allocated to you by Financial I (${users.length} members assigned).`}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: "left", md: "right" } }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
                width: "100%",
                justifyContent: { xs: "stretch", sm: "flex-start", md: "flex-end" }
              }}
            >
              <TextField
                size="small"
                placeholder="Search by name or reg #..."
                value={searchTitle}
                onChange={onChangeSearchTitle}
                sx={{
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                  minWidth: { xs: "100%", sm: 200 },
                  maxWidth: { md: 260 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#F8FAFC",
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#CBD5E1" },
                    "&.Mui-focused fieldset": { borderColor: "#0B3B60" },
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={retrieveUsers}
                  disabled={loading}
                  sx={{
                    flex: { xs: 1, sm: "initial" },
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#CBD5E1",
                    whiteSpace: "nowrap"
                  }}
                >
                  {loading ? <CircularProgress size={18} /> : "Refresh"}
                </Button>
                {!isPresident && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AccountBalanceWalletIcon />}
                    onClick={() => {
                      setSelectedBfMember(null);
                      setBfModalOpen(true);
                    }}
                    sx={{
                      flex: { xs: 2, sm: "initial" },
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      backgroundColor: "#0B3B60",
                      "&:hover": { backgroundColor: "#072740" },
                      whiteSpace: "nowrap"
                    }}
                  >
                    Opening Balances (BF)
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Financial I Member Allocation Panel */}
        {isSuperFinancial && !isBusiness && (
          <Card
            variant="outlined"
            sx={{
              mt: 3,
              backgroundColor: "#F8FAFC",
              borderRadius: 2,
              borderColor: "#E2E8F0",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ pb: "16px !important" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.75rem" }}>
                Member Allocation Manager (Financial I)
              </Typography>
              <Grid container spacing={2} alignItems="center">
                {/* Officer Selection */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="officer-select-label">Target Financial Officer</InputLabel>
                    <Select
                      labelId="officer-select-label"
                      label="Target Financial Officer"
                      value={selectedOfficerId}
                      onChange={(e) => setSelectedOfficerId(e.target.value)}
                      renderValue={(selectedId) => {
                        const off = officers.find((o) => (o._id || o.id) === selectedId);
                        if (!off) return "";
                        const offRoles = (off.roles || []).map((r) => (typeof r === "object" ? r.name : String(r)));
                        let roleLabel = "Financial";
                        if (offRoles.includes("financial_2")) roleLabel = "Financial II";
                        else if (offRoles.includes("financial_3")) roleLabel = "Financial III";
                        else if (offRoles.includes("financial") || offRoles.includes("financial_1")) roleLabel = "Financial I";
                        const name = off.firstName ? `${off.firstName} ${off.lastName || ""}`.trim() : off.username;
                        const count = off.assignedCount !== undefined ? off.assignedCount : 0;
                        return `${roleLabel}: ${name} (${count} assigned)`;
                      }}
                    >
                      {officers.map((off) => {
                        const offRoles = (off.roles || []).map((r) => (typeof r === "object" ? r.name : String(r)));
                        let roleLabel = "Financial";
                        if (offRoles.includes("financial_2")) roleLabel = "Financial II";
                        else if (offRoles.includes("financial_3")) roleLabel = "Financial III";
                        else if (offRoles.includes("financial") || offRoles.includes("financial_1")) roleLabel = "Financial I";
                        const name = off.firstName ? `${off.firstName} ${off.lastName || ""}`.trim() : off.username;
                        const count = off.assignedCount !== undefined ? off.assignedCount : 0;
                        return (
                          <MenuItem key={off._id || off.id} value={off._id || off.id}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                              <span>{roleLabel}: {name}</span>
                              <Chip
                                size="small"
                                label={`${count} assigned`}
                                variant="outlined"
                                color="primary"
                                sx={{ height: 20, fontSize: "0.7rem", ml: 1, fontWeight: 600 }}
                              />
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {(() => {
                      const sel = officers.find((o) => (o._id || o.id) === selectedOfficerId);
                      if (!sel) return null;
                      const count = sel.assignedCount !== undefined ? sel.assignedCount : 0;
                      return (
                        <FormHelperText sx={{ fontWeight: 600, color: "primary.main" }}>
                          Currently assigned: {count} member{count === 1 ? "" : "s"}
                        </FormHelperText>
                      );
                    })()}
                  </FormControl>
                </Grid>

                {/* Range Selection tool (e.g. 1-20) */}
                <Grid item xs={12} sm={6} md={4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ whiteSpace: "nowrap", fontWeight: 600, color: "#334155" }}>
                      Range:
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      label="From #"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      sx={{ width: 90 }}
                    />
                    <Typography variant="body2" sx={{ color: "#64748B" }}>to</Typography>
                    <TextField
                      size="small"
                      type="number"
                      label="To #"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      sx={{ width: 90 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSelectRange}
                      sx={{ whiteSpace: "nowrap", textTransform: "none", fontWeight: 600, borderRadius: "6px" }}
                    >
                      Select Range
                    </Button>
                  </Stack>
                </Grid>

                {/* Bulk Actions */}
                <Grid item xs={12} md={5}>
                  <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} flexWrap="wrap">
                    <Button size="small" onClick={handleSelectAll} variant="text" sx={{ textTransform: "none", fontWeight: 600 }}>
                      {selectedIds.size === users.length ? "Deselect All" : "Select All"}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={allocating || selectedIds.size === 0 || !selectedOfficerId}
                      onClick={() => handleAllocate("assign")}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px" }}
                    >
                      {allocating ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : `Assign (${selectedIds.size})`}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={allocating || selectedIds.size === 0 || !selectedOfficerId}
                      onClick={() => handleAllocate("unassign")}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px" }}
                    >
                      Unassign ({selectedIds.size})
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {isBusiness && !isAdmin && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5 }}>
            Business Officer Access: You have authorized permissions to manage transactions on the Business account only ({users.length} members total).
          </Alert>
        )}
        {/* Member list banner for Financial II & III */}
        {isRestrictedFinancial && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5 }}>
            You have restricted Financial access. Only members allocated to your account by Financial I are displayed here ({users.length} members total). You can manage Shares, Savings, Loans, Ileya, Dev. Levy, Minutes, Welfare, and Special accounts for these members.
          </Alert>
        )}

        {/* Filter by Assigned Officer (hidden for Business role) */}
        {!isBusiness && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mt: 2.5,
              mb: 1.5,
              p: 1.5,
              borderRadius: "10px",
              backgroundColor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
                Filter by Assigned:
              </Typography>

              <FormControl size="small" sx={{ minWidth: 260 }}>
                <Select
                  value={assignedFilter}
                  onChange={(e) => setAssignedFilter(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    "& .MuiSelect-select": { py: 0.65, px: 1.5 },
                  }}
                >
                  <MenuItem value="All">
                    All Members ({assignedCounts.All || 0})
                  </MenuItem>
                  <MenuItem value="unassigned">
                    Unassigned ({assignedCounts.unassigned || 0})
                  </MenuItem>
                  {availableOfficers.map((off) => {
                    const offId = String(off._id || off.id);
                    const count = assignedCounts[offId] || 0;
                    return (
                      <MenuItem key={offId} value={offId}>
                        {formatOfficerLabel(off)} ({count} members)
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {/* Quick Filter Chips */}
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ alignItems: "center" }}>
                <Chip
                  label={`All (${assignedCounts.All || 0})`}
                  size="small"
                  onClick={() => setAssignedFilter("All")}
                  color={assignedFilter === "All" ? "primary" : "default"}
                  variant={assignedFilter === "All" ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    backgroundColor: assignedFilter === "All" ? "#0B3B60" : "#ffffff",
                  }}
                />
                <Chip
                  label={`Unassigned (${assignedCounts.unassigned || 0})`}
                  size="small"
                  onClick={() => setAssignedFilter("unassigned")}
                  color={assignedFilter === "unassigned" ? "warning" : "default"}
                  variant={assignedFilter === "unassigned" ? "filled" : "outlined"}
                  sx={{ fontWeight: 700, fontSize: "0.75rem", cursor: "pointer" }}
                />
                {availableOfficers.map((off) => {
                  const offId = String(off._id || off.id);
                  const count = assignedCounts[offId] || 0;
                  const isSelected = assignedFilter === offId;
                  return (
                    <Chip
                      key={offId}
                      label={`${formatOfficerLabel(off)} (${count})`}
                      size="small"
                      onClick={() => setAssignedFilter(offId)}
                      color={isSelected ? "primary" : "default"}
                      variant={isSelected ? "filled" : "outlined"}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#0B3B60" : "#ffffff",
                        borderColor: isSelected ? "#0B3B60" : "#CBD5E1",
                        color: isSelected ? "#ffffff" : "#334155",
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            {assignedFilter !== "All" && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => setAssignedFilter("All")}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#64748B",
                  borderColor: "#CBD5E1",
                  borderRadius: "6px",
                }}
              >
                Clear Assigned Filter
              </Button>
            )}
          </Box>
        )}

        {/* Membership Status Filter Tabs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            p: 1.25,
            borderRadius: "10px",
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155", px: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
            Status Filter:
          </Typography>

          {[
            { label: "Active", count: statusCounts.Active, color: "#059669" },
            { label: "Non-Active", count: statusCounts["Non-Active"], color: "#D97706" },
            { label: "Withdraw", count: statusCounts.Withdraw, color: "#854D0E" },
            { label: "Expel", count: statusCounts.Expel, color: "#DC2626" },
            { label: "Decease", count: statusCounts.Decease, color: "#475569" },
            { label: "All", count: statusCounts.All, color: "#0B3B60" },
          ].map((tab) => {
            const isSelected = statusFilter === tab.label;
            return (
              <Button
                key={tab.label}
                size="small"
                variant={isSelected ? "contained" : "outlined"}
                onClick={() => setStatusFilter(tab.label)}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  px: 1.8,
                  py: 0.5,
                  backgroundColor: isSelected ? tab.color : "#ffffff",
                  borderColor: isSelected ? tab.color : "#CBD5E1",
                  color: isSelected ? "#ffffff" : "#334155",
                  boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                  "&:hover": {
                    backgroundColor: isSelected ? tab.color : "#F1F5F9",
                    borderColor: tab.color,
                  },
                }}
              >
                {tab.label} ({tab.count})
              </Button>
            );
          })}
        </Box>

        {/* Main Table */}
        <Box sx={{ mt: 1 }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              overflow: "auto",
              borderRadius: 2,
              border: "1px solid #E2E8F0",
            }}
          >
            <Table size="small" {...getTableProps()}>
              <TableHead sx={{ backgroundColor: "#0B3B60" }}>
                {headerGroups.map((headerGroup) => (
                  <TableRow key={headerGroup.getHeaderGroupProps().key} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <TableCell
                        key={column.getHeaderProps().key}
                        {...column.getHeaderProps(column.getSortByToggleProps ? column.getSortByToggleProps() : undefined)}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.8125rem",
                          color: "#ffffff",
                          backgroundColor: "#0B3B60",
                          borderBottom: "1px solid rgba(255,255,255,0.1)",
                          py: 1.5,
                          cursor: column.canSort ? "pointer" : "default",
                          userSelect: "none",
                        }}
                      >
                        {column.render("Header")}
                        {column.isSorted ? (column.isSortedDesc ? " ↓" : " ↑") : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 5, color: "#64748B" }}>
                      {loading ? <CircularProgress size={24} /> : "No members found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    const isSelected = selectedIds.has(row.original._id || row.original.id);
                    return (
                      <TableRow
                        hover
                        key={row.getRowProps().key}
                        {...row.getRowProps()}
                        selected={isSelected}
                        sx={{
                          "&:nth-of-type(even)": { backgroundColor: "#F8FAFC" },
                          "&:hover": { backgroundColor: "#F1F5F9 !important" },
                          "& td": { py: 1.1, borderColor: "#F1F5F9" },
                        }}
                      >
                        {row.cells.map((cell) => (
                          <TableCell key={cell.getCellProps().key} {...cell.getCellProps()} sx={{ py: 1 }}>
                            {cell.render("Cell")}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2.5,
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                First
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                Next
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                Last
              </Button>
            </Stack>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
                Page <strong style={{ color: "#0F172A" }}>{pageIndex + 1}</strong> of{" "}
                <strong style={{ color: "#0F172A" }}>{pageOptions.length || 1}</strong>
              </Typography>
              <TextField
                select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                sx={{
                  width: 130,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    fontSize: "0.8125rem",
                  },
                }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size} / page
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button
            component={Link}
            to="/dashboard"
            variant="contained"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 2.5 }}
          >
            Return to Dashboard
          </Button>
        </Box>
      </Paper>

      {/* Brought Forward (Opening Balances) Modal */}
      {!isPresident && (
        <BroughtForwardModal
          open={bfModalOpen}
          onClose={() => {
            setBfModalOpen(false);
            setSelectedBfMember(null);
          }}
          members={users}
          preselectedMember={selectedBfMember}
          onSuccess={() => {
            retrieveUsers();
            setSnackbar({
              open: true,
              message: "Opening balance(s) recorded successfully!",
              severity: "success",
            });
          }}
        />
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Admin;

// Helper: fetch latest transaction dates
async function fetchAllTransactionsForDisplay() {
  const results = {
    savings: [],
    loan: [],
    shares: [],
    ileya: [],
    dev: [],
    speacc: [],
    business: [],
    takaful: []
  };

  try {
    const res = await BusinessService.getAll();
    results.business = res && res.data ? res.data : (res || []);
  } catch (e) {
    results.business = [];
  }

  const maps = {};
  Object.keys(results).forEach((k) => {
    maps[k] = {};
    const items = results[k] || [];
    items.forEach((it) => {
      const uid = it.userId || it.user || it.user_id || it.userRef || it.memberId || it.membershipId || it.membership_id || it._user || it.owner;
      const candidate = uid || it.membershipId || it.membership_id || it.memberId || null;
      if (!candidate) return;
      const d = it.createdAt || it.date || it.timestamp || it.txDate || it.transactionDate || it.created_at || it.updatedAt || null;
      if (!d) return;
      const iso = new Date(d).toISOString();
      if (!maps[k][candidate] || new Date(maps[k][candidate]) < new Date(iso)) {
        maps[k][candidate] = iso;
      }
    });
  });

  // Track latest tenor for members who bought from business
  maps.businessTenor = {};
  const bizItems = results.business || [];
  const latestBizBuyDate = {};
  bizItems.forEach((it) => {
    const rawCod = String(it.creditOrDebit || "").toLowerCase();
    if (rawCod === "credit" || rawCod === "buy") {
      const uid = it.userId || it.user || it.user_id || it.userRef || it.memberId || it.membershipId || it.membership_id || it._user || it.owner;
      const candidate = uid || it.membershipId || it.membership_id || it.memberId || null;
      if (!candidate) return;
      const d = it.createdAt || it.date || it.timestamp || it.txDate || it.transactionDate || it.created_at || it.updatedAt || null;
      const iso = d ? new Date(d).toISOString() : "1970-01-01";
      if (it.tenor && (!latestBizBuyDate[candidate] || new Date(latestBizBuyDate[candidate]) <= new Date(iso))) {
        latestBizBuyDate[candidate] = iso;
        maps.businessTenor[candidate] = it.tenor;
      }
    }
  });

  // Track latest tenor for members who took a loan (Approved Loan / credit)
  maps.loanTenor = {};
  const loanItems = results.loan || [];
  const latestLoanCreditDate = {};
  loanItems.forEach((it) => {
    const rawCod = String(it.creditOrDebit || "").toLowerCase();
    if (rawCod === "credit" || rawCod === "approved loan" || rawCod === "get loan") {
      const uid = it.userId || it.user || it.user_id || it.userRef || it.memberId || it.membershipId || it.membership_id || it._user || it.owner;
      const candidate = uid || it.membershipId || it.membership_id || it.memberId || null;
      if (!candidate) return;
      const tenorVal = it.tenorMonths || it.tenor;
      const d = it.createdAt || it.date || it.timestamp || it.txDate || it.transactionDate || it.created_at || it.updatedAt || null;
      const iso = d ? new Date(d).toISOString() : "1970-01-01";
      if (tenorVal && (!latestLoanCreditDate[candidate] || new Date(latestLoanCreditDate[candidate]) <= new Date(iso))) {
        latestLoanCreditDate[candidate] = iso;
        maps.loanTenor[candidate] = tenorVal;
      }
    }
  });

  return maps;
}

async function fetchAllTransactions(users, setLastTx) {
  if (!users || users.length === 0) {
    setLastTx({
      savings: {}, loan: {}, shares: {}, ileya: {}, dev: {}, min: {}, wel: {}, takaful: {}, speacc: {}, business: {}, businessTenor: {}, loanTenor: {}
    });
    return;
  }
  const maps = await fetchAllTransactionsForDisplay();
  const resultMaps = {};
  Object.keys(maps).forEach((k) => (resultMaps[k] = {}));
  users.forEach((u) => {
    const uid = u._id || u.id || u.membershipId;
    Object.keys(maps).forEach((k) => {
      const byUid = maps[k][uid];
      const byMem = (u.membershipId && maps[k][u.membershipId]) || (u.membership_id && maps[k][u.membership_id]);
      resultMaps[k][u._id] = byUid || byMem || null;
    });
  });
  setLastTx(resultMaps);
}
